import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Award, Calendar, TrendingUp, Gift, Check, Lock, Sparkles, Crown, Star, Trophy, Gem, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import axios from "axios";
import StreakFreezeDialog from "@/components/premium/StreakFreezeDialog";

import { BACKEND_URL as API } from "@/config/api";

// Reward icons mapping
const REWARD_ICONS = {
  "streak_3": Flame,
  "streak_7": Trophy,
  "streak_14": Star,
  "streak_30": Crown,
  "streak_60": Sparkles,
  "streak_100": Gem,
};

const StreakBadge = ({ className }) => {
  const [streakData, setStreakData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [claimingReward, setClaimingReward] = useState(null);
  const [newRewardUnlocked, setNewRewardUnlocked] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [savingStreak, setSavingStreak] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [showFreezeDialog, setShowFreezeDialog] = useState(false);

  const fetchStreakData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${API}/api/auth/streak`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching streak:", error);
      return null;
    }
  };

  const fetchSaveStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API}/api/premium/streak/saves`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaveStatus(res.data);
    } catch (error) {
      console.debug("Streak save status fetch failed:", error.message);
    }
  };

  const handleProtectStreak = async () => {
    setSavingStreak(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/premium/streak/save`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      setSaveStatus(prev => prev ? { ...prev, saves_remaining: res.data.saves_remaining, can_save_streak: res.data.saves_remaining > 0 } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to protect streak");
    } finally {
      setSavingStreak(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const res = await axios.get(`${API}/api/premium/streak/leaderboard`);
      setLeaderboard(res.data.leaderboard || []);
    } catch (e) {
      console.debug("Leaderboard fetch failed:", e.message);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const handleToggleFrame = async (enabled) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/premium/streak/frame`, { enabled }, { headers: { Authorization: `Bearer ${token}` } });
      setStreakData(prev => prev ? { ...prev, streak_frame_enabled: enabled } : prev);
      toast.success(enabled ? "Flame frame activated!" : "Flame frame disabled");
      window.dispatchEvent(new Event('duolyst:refresh-entitlements'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle frame");
    }
  };

  const handleSetColor = async (color) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/premium/streak/color`, { color }, { headers: { Authorization: `Bearer ${token}` } });
      setSaveStatus(prev => prev ? { ...prev } : prev);
      toast.success("Streak color updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set color");
    }
  };

  const handleTogglePublic = async (pub) => {
    // Optimistic UI update so the user sees the toggle move immediately
    setStreakData((prev) => prev ? { ...prev, streak_public: pub } : prev);
    try {
      await axios.put(`${API}/api/premium/streak/public`, { public: pub });
      toast.success(pub ? "Streak visible on leaderboard!" : "Streak hidden from leaderboard");
      // Refresh authoritative state
      const fresh = await fetchStreakData();
      if (fresh) setStreakData(fresh);
    } catch (error) {
      // Revert
      setStreakData((prev) => prev ? { ...prev, streak_public: !pub } : prev);
      toast.error(error.response?.data?.detail || "Failed to update visibility");
    }
  };

  useEffect(() => {
    const fetchAndRecordVisit = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        // Get current streak data first
        const beforeData = await fetchStreakData();

        // Record visit and get updated streak
        const response = await axios.post(
          `${API}/api/auth/record-visit`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Fetch full streak data with rewards
        const fullData = await fetchStreakData();
        setStreakData(fullData);

        // Streak save was just consumed — let the user know their save worked
        if (response.data.protection_consumed === 'streak_save') {
          toast.success(
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <div>
                <div className="font-semibold">Streak Saved!</div>
                <div className="text-xs opacity-80">Your {response.data.current_streak - 1}-day streak is intact — your save was consumed.</div>
              </div>
            </div>,
            { duration: 6000 }
          );
        } else if (fullData?.protection_consumed_recently) {
          // Fallback: user opened a fresh tab after save was consumed at login
          toast.success(
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <div>
                <div className="font-semibold">Streak Saved!</div>
                <div className="text-xs opacity-80">Your streak is safe. Your save has been used.</div>
              </div>
            </div>,
            { duration: 6000 }
          );
        }

        // Show celebration if streak increased
        if (response.data.streak_increased && response.data.new_day) {
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 3000);

          // Check if a new reward was unlocked
          if (fullData?.rewards) {
            const newlyUnlocked = fullData.rewards.find(
              r => r.unlocked && r.can_claim && 
              (!beforeData?.rewards?.find(br => br.id === r.id)?.unlocked)
            );
            if (newlyUnlocked) {
              setNewRewardUnlocked(newlyUnlocked);
              setTimeout(() => {
                setShowDetails(true);
              }, 1500);
            }
          }
        }
      } catch (error) {
        console.error("Error recording visit:", error);
        const fallbackData = await fetchStreakData();
        setStreakData(fallbackData);
      }
    };

    fetchAndRecordVisit();
  }, []);

  const handleClaimReward = async (rewardId) => {
    setClaimingReward(rewardId);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/auth/streak/claim/${rewardId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-2xl">{response.data.emoji}</span>
          <div>
            <p className="font-semibold">{response.data.title} Claimed!</p>
            <p className="text-sm text-muted-foreground">{response.data.message}</p>
          </div>
        </div>,
        { duration: 5000 }
      );

      // Refresh streak data
      const updatedData = await fetchStreakData();
      setStreakData(updatedData);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim reward");
    } finally {
      setClaimingReward(null);
    }
  };

  if (!streakData) return null;

  const { current_streak, longest_streak, total_visits, streak_active, rewards, next_reward, claimed_rewards, streak_public } = streakData;

  // Count claimable rewards
  const claimableCount = rewards?.filter(r => r.can_claim).length || 0;

  // Determine flame color based on streak
  const getFlameColor = () => {
    if (current_streak >= 100) return "text-purple-500";
    if (current_streak >= 60) return "text-pink-500";
    if (current_streak >= 30) return "text-yellow-500";
    if (current_streak >= 14) return "text-orange-500";
    if (current_streak >= 7) return "text-orange-400";
    if (current_streak >= 3) return "text-orange-300";
    return "text-gray-400";
  };

  const getFlameAnimation = () => {
    if (current_streak >= 7) return "animate-pulse";
    return "";
  };

  // Get active perks
  const activePerks = rewards?.filter(r => r.claimed) || [];

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              onClick={() => { setShowDetails(true); fetchSaveStatus(); fetchLeaderboard(); }}
              className={cn(
                "relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-full",
                "bg-background/80 backdrop-blur-sm border border-border/50",
                "hover:bg-accent/50 transition-colors cursor-pointer",
                className
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Flame
                className={cn(
                  "w-4 h-4 transition-colors",
                  getFlameColor(),
                  getFlameAnimation()
                )}
              />
              <span className="text-sm font-semibold text-foreground">
                {current_streak}
              </span>

              {/* Claimable reward indicator */}
              {claimableCount > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-secondary rounded-full flex items-center justify-center"
                >
                  <Gift className="w-2.5 h-2.5 text-white" />
                </motion.div>
              )}

              {/* Celebration particles */}
              <AnimatePresence>
                {showCelebration && (
                  <>
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={`celebration-particle-${i}`}
                        className="absolute w-2 h-2 rounded-full bg-orange-400"
                        initial={{ opacity: 1, scale: 0 }}
                        animate={{
                          opacity: [1, 0],
                          scale: [0, 1],
                          x: Math.cos((i * Math.PI) / 4) * 30,
                          y: Math.sin((i * Math.PI) / 4) * 30,
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      />
                    ))}
                  </>
                )}
              </AnimatePresence>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-sm">
              {current_streak} day streak!
              {claimableCount > 0 && ` (${claimableCount} reward${claimableCount > 1 ? 's' : ''} to claim!)`}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Streak Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className={cn("w-6 h-6", getFlameColor())} />
              Your Visit Streak
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="rewards" className="relative">
                Rewards
                {claimableCount > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-secondary text-white rounded-full">
                    {claimableCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="leaderboard">Top 20</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="flex-1 overflow-y-auto space-y-6 py-4">
              {/* Current Streak */}
              <div className="text-center">
                <motion.div
                  className="relative inline-block"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                >
                  <div className={cn(
                    "w-24 h-24 rounded-full flex items-center justify-center",
                    "bg-gradient-to-br from-orange-400 to-red-500",
                    "shadow-lg shadow-orange-500/30"
                  )}>
                    <div className="text-center">
                      <Flame className="w-8 h-8 text-white mx-auto mb-1" />
                      <span className="text-2xl font-bold text-white">{current_streak}</span>
                    </div>
                  </div>
                  
                  {/* Active perk badges around the circle */}
                  {activePerks.slice(0, 3).map((perk, i) => (
                    <motion.div
                      key={perk.id}
                      className="absolute w-8 h-8 rounded-full bg-background border-2 border-primary flex items-center justify-center text-sm"
                      style={{
                        top: i === 0 ? -8 : i === 1 ? 60 : 60,
                        left: i === 0 ? 40 : i === 1 ? -8 : 88,
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      {perk.emoji}
                    </motion.div>
                  ))}
                </motion.div>
                <p className="text-lg font-medium text-foreground mt-3">
                  {current_streak === 0
                    ? "Start your streak!"
                    : current_streak === 1
                    ? "1 Day Streak"
                    : `${current_streak} Day Streak`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {streak_active
                    ? "Keep it going! Visit tomorrow to continue."
                    : "Visit daily to build your streak!"}
                </p>
              </div>

              {/* Next Reward Progress */}
              {next_reward && (
                <div className="bg-accent/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Next: {next_reward.emoji} {next_reward.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {next_reward.days_remaining} days to go
                    </span>
                  </div>
                  <Progress 
                    value={(current_streak / next_reward.days) * 100} 
                    className="h-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {current_streak} / {next_reward.days} days
                  </p>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-accent/30 rounded-xl p-3 text-center">
                  <Award className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-foreground">{longest_streak}</p>
                  <p className="text-xs text-muted-foreground">Best Streak</p>
                </div>
                <div className="bg-accent/30 rounded-xl p-3 text-center">
                  <Calendar className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-foreground">{total_visits}</p>
                  <p className="text-xs text-muted-foreground">Total Visits</p>
                </div>
                <div className="bg-accent/30 rounded-xl p-3 text-center">
                  <Gift className="w-5 h-5 text-secondary mx-auto mb-1" />
                  <p className="text-xl font-bold text-foreground">{claimed_rewards?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Rewards</p>
                </div>
              </div>

              {/* Active Perks */}
              {activePerks.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" /> Your Badges
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {activePerks.map((perk) => (
                      <div
                        key={perk.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-full bg-primary/10 border border-primary/30"
                      >
                        <span>{perk.emoji}</span>
                        <span className="text-sm font-medium text-primary">{perk.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Streak Protection — saves are reactive (restore-on-break) + freeze is proactive (vacation mode) */}
              {saveStatus && current_streak > 0 && (
                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <p className="text-sm font-semibold text-foreground">Streak Protection</p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    <strong className="text-foreground">{saveStatus.saves_remaining}</strong> of {saveStatus.monthly_limit} Streak Saves left this month. If you miss a day, we'll let you restore your streak with one tap.
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    {Array.from({ length: saveStatus.monthly_limit }).map((_, i) => (
                      <div
                        key={i}
                        className={cn(
                          "h-2 flex-1 rounded-full",
                          i < saveStatus.saves_remaining ? "bg-blue-500" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                  <Button
                    onClick={() => setShowFreezeDialog(true)}
                    size="sm"
                    variant="outline"
                    className="w-full rounded-xl h-10 border-cyan-500/40 text-cyan-600 hover:bg-cyan-500/10"
                    data-testid="streak-freeze-open-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Going away? Freeze my streak
                  </Button>
                  {saveStatus.saves_remaining === 0 && (
                    <p className="text-xs text-center text-muted-foreground mt-3">
                      All saves used this month. Resets {new Date(saveStatus.resets_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}.
                      {saveStatus.upgrade_benefit && <span className="block mt-1 text-primary">{saveStatus.upgrade_benefit}</span>}
                    </p>
                  )}
                </div>
              )}

              {/* Flame Frame Toggle — requires streak_7 */}
              {claimed_rewards?.includes("streak_7") && (
                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Flame Frame</p>
                        <p className="text-xs text-muted-foreground">Animated glow around your avatar</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleFrame(!streakData?.streak_frame_enabled)}
                      className={cn(
                        "w-11 h-6 rounded-full transition-colors relative",
                        streakData?.streak_frame_enabled ? "bg-orange-500" : "bg-muted"
                      )}
                    >
                      <div className={cn(
                        "w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform",
                        streakData?.streak_frame_enabled ? "translate-x-[22px]" : "translate-x-0.5"
                      )} />
                    </button>
                  </div>
                  {current_streak < 7 && (
                    <p className="text-xs text-orange-400 mt-2">Need active 7+ day streak for the frame</p>
                  )}
                </div>
              )}

              {/* Custom Streak Color — requires streak_14 */}
              {claimed_rewards?.includes("streak_14") && (
                <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-xl p-4">
                  <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-purple-500" /> Streak Theme Color
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {["#E07A5F","#81B29A","#F2CC8F","#2D9CDB","#9B51E0","#EB5757","#27AE60","#F2994A","#BB6BD9","#56CCF2"].map(c => (
                      <button
                        key={c}
                        onClick={() => handleSetColor(c)}
                        className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 border-transparent"
                        style={{ backgroundColor: c, borderColor: saveStatus?.current_color === c ? '#fff' : 'transparent', transform: saveStatus?.current_color === c ? 'scale(1.15)' : undefined }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Public Leaderboard Toggle — requires streak_30 */}
              {claimed_rewards?.includes("streak_30") && (
                <div
                  className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border border-amber-500/20 rounded-xl p-4"
                  data-testid="public-streak-toggle-row"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Public Streak</p>
                        <p className="text-xs text-muted-foreground">Show on the Top 20 leaderboard</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleTogglePublic(!streak_public)}
                      aria-pressed={!!streak_public}
                      data-testid="public-streak-toggle-btn"
                      className={cn(
                        "w-11 h-6 rounded-full transition-colors relative",
                        streak_public ? "bg-amber-500" : "bg-muted"
                      )}
                    >
                      <div
                        className={cn(
                          "w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform",
                          streak_public ? "translate-x-[22px]" : "translate-x-0.5"
                        )}
                      />
                    </button>
                  </div>
                </div>
              )}

            </TabsContent>

            <TabsContent value="rewards" className="flex-1 overflow-y-auto py-4">
              <div className="space-y-3">
                {rewards?.map((reward, index) => {
                  const RewardIcon = REWARD_ICONS[reward.id] || Gift;
                  
                  return (
                    <motion.div
                      key={reward.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={cn(
                        "relative p-4 rounded-xl border transition-all",
                        reward.claimed 
                          ? "bg-primary/5 border-primary/30" 
                          : reward.unlocked 
                          ? "bg-secondary/10 border-secondary/50 shadow-md" 
                          : "bg-muted/30 border-border opacity-60"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center text-xl",
                          reward.claimed 
                            ? "bg-primary/20" 
                            : reward.unlocked 
                            ? "bg-secondary/20" 
                            : "bg-muted"
                        )}>
                          {reward.claimed ? (
                            <Check className="w-6 h-6 text-primary" />
                          ) : reward.unlocked ? (
                            <span>{reward.emoji}</span>
                          ) : (
                            <Lock className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-foreground">{reward.title}</p>
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              reward.type === "badge" && "bg-blue-500/20 text-blue-600",
                              reward.type === "perk" && "bg-purple-500/20 text-purple-600",
                              reward.type === "feature" && "bg-orange-500/20 text-orange-600",
                              reward.type === "title" && "bg-pink-500/20 text-pink-600",
                              reward.type === "honor" && "bg-yellow-500/20 text-yellow-600",
                            )}>
                              {reward.type}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {reward.description}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {reward.days}-day streak required
                          </p>
                        </div>
                        
                        {reward.can_claim && (
                          <Button
                            size="sm"
                            onClick={() => handleClaimReward(reward.id)}
                            disabled={claimingReward === reward.id}
                            className="bg-secondary hover:bg-secondary/90 text-white rounded-full"
                          >
                            {claimingReward === reward.id ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1 }}
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              />
                            ) : (
                              <>
                                <Gift className="w-4 h-4 mr-1" />
                                Claim
                              </>
                            )}
                          </Button>
                        )}
                        
                        {reward.claimed && (
                          <div className="flex items-center gap-1 text-primary">
                            <Check className="w-4 h-4" />
                            <span className="text-xs font-medium">Claimed</span>
                          </div>
                        )}
                      </div>

                      {/* Progress bar for locked rewards */}
                      {!reward.unlocked && (
                        <div className="mt-3">
                          <Progress 
                            value={(longest_streak / reward.days) * 100} 
                            className="h-1.5"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {longest_streak} / {reward.days} days ({Math.round((longest_streak / reward.days) * 100)}%)
                          </p>
                        </div>
                      )}

                      {/* New unlock indicator */}
                      {newRewardUnlocked?.id === reward.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="absolute -top-2 -right-2 px-2 py-1 bg-secondary text-white text-xs font-bold rounded-full"
                        >
                          NEW!
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="leaderboard" className="flex-1 overflow-y-auto py-4">
              {loadingLeaderboard ? (
                <div className="flex items-center justify-center py-12">
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-12">
                  <Trophy className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No public streaks yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Reach a 30-day streak to join!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry) => (
                    <div key={entry.rank} className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border",
                      entry.rank <= 3 ? "bg-accent/20 border-accent/30" : "bg-muted/20 border-border"
                    )}>
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                        entry.rank === 1 ? "bg-yellow-500 text-white" :
                        entry.rank === 2 ? "bg-slate-400 text-white" :
                        entry.rank === 3 ? "bg-amber-700 text-white" :
                        "bg-muted text-muted-foreground"
                      )}>
                        {entry.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-foreground truncate">{entry.name}</p>
                          {entry.founding_explorer && <Crown className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />}
                          {entry.streak_legend_title && <span className="text-xs px-1.5 py-0.5 bg-pink-500/20 text-pink-500 rounded-full flex-shrink-0">Legend</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <Flame className="w-4 h-4 text-orange-500" />
                          <span className="text-lg font-bold text-foreground">{entry.current_streak}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Best: {entry.longest_streak}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Vacation-mode streak freeze (premium) */}
      <StreakFreezeDialog
        open={showFreezeDialog}
        onOpenChange={setShowFreezeDialog}
        onFrozen={() => { fetchSaveStatus(); }}
      />
    </>
  );
};

export default StreakBadge;
